import React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Tooltip,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';

import config from 'config';
import { TableBox } from 'retired/Box';
import { selectDeedById } from 'entities/deed/selectors';
import { selectUser } from 'entities/user/selectors';
import { currenciesFormat } from 'utils/currencyFormat';
import Api from 'entities/api';
import { Privileges } from 'utils/privileges';

import { getFormsListString } from './utils';

type RouteParams = {
  id: string;
  type: string;
};

interface Props {
  history: any;
  matchedAmountUserCurrency: any;
  OriginalCurrencyTooltip: any;
  differentCurrency: any;
  regularDeedAccess: boolean;
}

const FormsListTableCell = styled(TableCell)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
`;

export const DeedDonorTable = ({
  history,
  matchedAmountUserCurrency,
  OriginalCurrencyTooltip,
  differentCurrency,
  regularDeedAccess,
}: Props) => {
  const { id } = useParams<RouteParams>();
  const deed = useSelector((state) => selectDeedById(state, id));
  const user = useSelector(selectUser);

  const canViewDonationDetails = user?.hasPrivilege(Privileges.DONATION_VIEW);

  const csvDonationsDownloadBtn = regularDeedAccess && (
    <>
      {/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/anchor-is-valid */}
      <a
        style={{ marginLeft: 5, outline: 'none' }}
        onClick={(e) => {
          const a = e.target.tagName === 'A' ? e.target : e.target.parentElement;
          if (!a.href) {
            fetch(`${config.apiUrl}/api/donations/deeds/${deed.id}?export=1`, {
              method: 'GET',
              headers: {
                Authorization: Api.defaultHeaders.Authorization,
              },
            })
              .then((response) => response.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = `donations_${deed.name}_${new Date().toISOString()}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                a.removeAttribute('href');
              })
              .catch(() => {
                // Error is intentionally ignored, maybe it should be logged?
              });
          }
        }}
        role="button"
        tabIndex="0"
        title="Download CSV file"
      >
        Export All
      </a>
      {/* eslint-enable jsx-a11y/click-events-have-key-events,jsx-a11y/anchor-is-valid */}
    </>
  );

  const onLinkClick = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      {deed.donationsCount ? (
        <TableBox style={{ overflowX: 'auto' }}>
          <Table size="small" className="colored purpleLighten20">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {canViewDonationDetails && <TableCell>Donation</TableCell>}
                {!!matchedAmountUserCurrency && canViewDonationDetails && (
                  <TableCell>Match</TableCell>
                )}
                <TableCell>Donated on</TableCell>
                {deed.forms?.size > 0 &&
                deed.forms.toArray().filter((form) => form.responses.length).length ? (
                  <TableCell>Forms</TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {deed.donations.map((donation) => {
                if (!donation) {
                  return null;
                }
                const donationUser = donation.get('user');
                const donationUserName =
                  donationUser &&
                  `${donationUser.get('fullName').get('first')} ${donationUser
                    .get('fullName')
                    .get('last')}`;
                return (
                  <TableRow
                    key={donation.get('id')}
                    onClick={() => history.push(`/donations/${donation.get('id')}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell
                      style={{
                        width: '30%',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        lineHeight: '32px',
                      }}
                    >
                      <Avatar
                        alt={donationUserName}
                        src={donationUser?.get('mainPicture').replace('http://', 'https://')}
                        style={{
                          width: 32,
                          height: 32,
                          float: 'left',
                          marginRight: 10,
                        }}
                      />
                      <Link to={`/volunteers/${donationUser?.get('id')}`} onClick={onLinkClick}>
                        {donationUserName}
                      </Link>
                    </TableCell>
                    {canViewDonationDetails && (
                      <>
                        <TableCell
                          style={{
                            width: '20%',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          <OriginalCurrencyTooltip
                            {...(differentCurrency
                              ? {
                                  title: currenciesFormat(
                                    donation.get('amountCurrencies')?.toJS(),
                                    donation.get('currency')
                                  ),
                                }
                              : {})}
                          >
                            <span>
                              {currenciesFormat(
                                donation.get('amountCurrencies')?.toJS(),
                                user.displayCurrency
                              )}
                              {differentCurrency ? '*' : ''}
                            </span>
                          </OriginalCurrencyTooltip>
                        </TableCell>
                        {!!matchedAmountUserCurrency && (
                          <TableCell
                            style={{
                              width: '20%',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}
                          >
                            <OriginalCurrencyTooltip
                              {...(differentCurrency
                                ? {
                                    title: `${
                                      donation
                                        .get('matchedAmountCurrencies')
                                        ?.get(user.displayCurrency)
                                        ? `${currenciesFormat(
                                            donation.get('matchedAmountCurrencies')?.toJS(),
                                            donation.get('currency')
                                          )}`
                                        : ''
                                    }`,
                                  }
                                : {})}
                            >
                              <span>
                                {donation.get('matchedAmountCurrencies')?.get(user.displayCurrency)
                                  ? `${currenciesFormat(
                                      donation.get('matchedAmountCurrencies')?.toJS(),
                                      user.displayCurrency
                                    )}${differentCurrency ? '*' : ''}`
                                  : ''}
                              </span>
                            </OriginalCurrencyTooltip>
                          </TableCell>
                        )}
                      </>
                    )}
                    <TableCell style={{ width: '20%', minWidth: 130 }}>
                      {moment(donation.get('date')).format('MMM DD, YYYY')}
                    </TableCell>
                    {deed.forms?.size > 0 &&
                    deed.forms.filter((form) => form.responses.length).size ? (
                      <FormsListTableCell>
                        {donationUser ? (
                          <Tooltip
                            title={getFormsListString(deed.forms.toArray(), donationUser.get('id'))}
                          >
                            <span>
                              {getFormsListString(deed.forms.toArray(), donationUser.get('id'))}
                            </span>
                          </Tooltip>
                        ) : null}
                      </FormsListTableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  size="medium"
                  colSpan={3}
                  style={{
                    color: '#0000008a',
                    fontSize: '0.875rem',
                  }}
                >
                  Total: {deed.donationsCount}
                  {csvDonationsDownloadBtn}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableBox>
      ) : null}
    </>
  );
};
